import {encodeParams} from './encodeParams';
import assert from 'assert-ts';
import {HttpError} from '../../types';

type Headers = {[key: string]: string};

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

type HttpArgs<
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends {[key: string]: string | number | undefined} = {}
> = {
  method: 'GET' | 'PATCH' | 'POST' | 'DELETE';
  baseUrl: string;
  endPoint: string;
  params?: TParameters | string;
  body?: Record<string, unknown> | undefined;
  extraHeaders?: Headers | undefined;
  accessToken?: string | undefined;
  /**
   * Default: true
   */
  responseAsJson?: boolean;
};

export function httpMethod<TData>(args: HttpArgs<{[key: string]: string}>) {
  const {
    method,
    baseUrl,
    endPoint,
    params,
    body,
    extraHeaders = {},
    accessToken,
    responseAsJson = true,
  } = args;

  assert(baseUrl, 'httpMethod: baseUrl must be given', args);

  assert(endPoint, 'httpMethod: endPoint must be given', args);

  let url: string = baseUrl + endPoint;

  // TODO: Path expansion (for id)

  if (params) {
    const encodedParams: string = encodeParams(params);
    url += encodedParams ? `?${encodedParams}` : '';
  }

  let bodyJson: string | undefined;
  if (body) {
    bodyJson = JSON.stringify(body);
  }

  const headers = accessToken
    ? {
        ...defaultHeaders,
        Authorization: `Bearer ${accessToken}`,
        ...extraHeaders,
      }
    : {...defaultHeaders, ...extraHeaders};

  console.log('HTTP requesting', method, url, params, body);

  return fetch(url, {
    method,
    body: bodyJson,
    headers,
  })
    .catch(error => {
      throw new HttpError(
        408,
        endPoint,
        error.message,
        'request.failed',
        undefined,
        error,
      );
    })
    .then(async response => {
      if (response.ok) {
        if (responseAsJson) {
          const data = (await response.json()) as TData;
          return data;
        } else if (response.status === 204) {
          return;
        } else {
          assert(
            false,
            'httpMethod: support {responseAsJson: false} & status !== 204 not implemented',
          );
        }
      } else {
        let errorInfo: any | undefined = {};
        try {
          errorInfo = await response.json();
        } catch (jsonError) {
          console.log(
            `HTTP ${method}: status: ${response.status}: url: ${url}, could not get json response`,
          );
        }

        throw new HttpError(
          response.status,
          endPoint,
          `HTTP ${method} failed: status: ${response.status}`,
          errorInfo.errorKey,
          errorInfo,
          undefined,
        );
      }
    })
    .catch(error => {
      if (error instanceof HttpError) {
        console.error(
          `HTTP ${method} ${args.endPoint} failed: ${
            error.status ?? 'unknown'
          }: ${error.errorKey ?? error.message ?? ''}`,
          error.status ?? undefined,
          error.errorKey,
          error.message,
          method,
          url,
          params,
          bodyJson,
        );
      } else {
        console.error(
          'HTTP request failed ',
          error.status ?? undefined,
          error.errorKey,
          error.message,
          method,
          url,
          params,
          bodyJson,
        );
      }

      throw error;
    });
}
